import { useState, useRef, useCallback, useEffect } from "react";
import Webcam from "react-webcam";
import { Box, Button, LinearProgress, Stack } from "@mui/material";
import styled from "@emotion/styled";
import FilePreview from "./components/FilePreview";
import Uploaders from "./components/Uploaders";
import { FileType } from "src/api/types";
import { useTranslation } from "react-i18next";
import { resizeImage } from "src/utils/resizeImage";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetFileQuery } from "src/api/services/bucketApi";

const convertToImageType = async (
  file: File | Blob,
  imageType: string = "image/jpeg",
) => {
  if (["image/heic", "image/heif"].includes(file.type)) {
    const { default: heic2any } = await import("heic2any");

    const result = await heic2any({
      blob: file,
      toType: imageType,
    });

    if (Array.isArray(result)) {
      return result[0];
    }

    return result;
  }

  return file;
};

const videoConstraints = {
  facingMode: "user",
};

export const ImageCol = styled.div`
  position: relative;
  width: 100%;
`;

const buttonStyles = {
  height: "52px",
  padding: { sm: "0 32px", xs: "0 8px" },
  textTransform: "none",
  fontSize: { sm: "1rem", xs: "0.75rem" },
};

interface UploaderProps {
  title: string | null;
  subtitle: string | null;
  name: string;
  file: FileType | null;
  folder: string;
  showConfirmationText?: boolean;
  setFilePath: (file: FileType | null) => void;
  subImageTextOne?: string | null;
  subImageTextTwo?: string | null;
  subImageTextMobile?: string | null;
}

const ImageUploader = ({
  title,
  subtitle,
  file,
  setFilePath,
  name,
  folder,
  subImageTextOne = null,
  subImageTextTwo = null,
  subImageTextMobile = null,
}: UploaderProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [takingWebcamPhoto, setTakingWebcamPhoto] = useState(false);
  const webcamRef = useRef<Webcam>(null);
  const { data, isLoading: fileLoading } = useGetFileQuery(
    name ? { fileName: name, folderName: folder } : skipToken,
  );

  const { t } = useTranslation();
  const capture = useCallback(() => {
    return webcamRef.current?.getScreenshot();
  }, [webcamRef]);

  const activateWebcam = useCallback(() => {
    setTakingWebcamPhoto(true);
  }, []);

  const disableWebcam = useCallback(() => {
    setTakingWebcamPhoto(false);
  }, []);

  const deleteFile = useCallback(() => {
    setFilePath(null);
  }, [setFilePath]);

  const captureWebcamPhoto = useCallback(() => {
    const screenshot = capture();

    if (screenshot) {
      setFilePath({
        fileToUpload: screenshot,
        type: "image/jpeg",
        name,
      });
      disableWebcam();
    }
  }, [capture, disableWebcam, name, setFilePath]);

  const handleFile = useCallback(
    async (file: File | Blob) => {
      const blob = await convertToImageType(file);

      resizeImage(blob, 1024).then((base64) => {
        setFilePath({
          fileToUpload: base64,
          type: file.type,
          name,
        });
      });
    },
    [name, setFilePath],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach(async (currentFile) => {
        if (
          [
            "image/png",
            "image/jpeg",
            "image/webp",
            "image/heic",
            "image/heif",
          ].includes(currentFile.type)
        ) {
          setIsLoading(true);
          try {
            await handleFile(currentFile);
          } finally {
            setIsLoading(false);
          }
        }
      });
    },
    [handleFile],
  );

  useEffect(() => {
    setFilePath(
      data
        ? {
            fileToUpload: data.url,
            name: data.name,
            type: data.type,
          }
        : null,
    );
  }, [data, setFilePath]);

  return (
    <>
      <Box height={4}>{fileLoading ? <LinearProgress /> : null}</Box>
      <Stack gap="5px" alignItems="center">
        <ImageCol>
          <Box>
            {takingWebcamPhoto ? (
              <Stack
                direction="row"
                overflow="hidden"
                borderRadius="24px"
                width="100%"
                height="260px"
                justifyContent="center"
                alignItems="center"
                border="1px dashed #1B1B1B"
                sx={{ cursor: "pointer" }}
                onClick={activateWebcam}
              >
                <Webcam
                  style={{
                    width: "fit-content",
                    height: "inherit",
                  }}
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                  videoConstraints={videoConstraints}
                />
              </Stack>
            ) : file ? (
              <FilePreview
                onRemove={deleteFile}
                file={file.fileToUpload}
                title={title || ""}
              />
            ) : (
              <Stack gap={4}>
                <Uploaders
                  title={title}
                  subtitle={subtitle}
                  activateWebcam={activateWebcam}
                  onDrop={onDrop}
                  subImageTextOne={subImageTextOne}
                  subImageTextTwo={subImageTextTwo}
                  subImageTextMobile={subImageTextMobile}
                  isLoading={isLoading}
                />
              </Stack>
            )}
          </Box>
        </ImageCol>
        <Box display="flex" gap="16px" justifyContent="center" width="100%">
          {takingWebcamPhoto && !file && (
            <Box display="flex" gap="16px" justifyContent="center" width="100%">
              <Button
                fullWidth
                variant="outlined"
                onClick={captureWebcamPhoto}
                sx={buttonStyles}
              >
                {t("button.take_a_photo", "Take a photo")}
              </Button>
              <Button
                fullWidth
                variant="secondary"
                onClick={disableWebcam}
                sx={buttonStyles}
              >
                {t("button.cancel", "Cancel")}
              </Button>
            </Box>
          )}
        </Box>
      </Stack>
    </>
  );
};

export default ImageUploader;
